import { GoogleGenAI } from "@google/genai";

const API_KEY = process.env.REACT_APP_GEMINI_API_KEY;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:8787/';

if (!API_KEY) {
  throw new Error("REACT_APP_GEMINI_API_KEY environment variable not set");
}

// Color mapping for node types
const nodeColorMap = {
  'TRIGGER': '#FF6B6B',
  'MARKET_RESEARCH': '#4ECDC4',
  'REGISTER_BUSINESS': '#45B7D1',
  'COMPLIANCE_CHECK': '#FFA07A',
  'HIRE_EMPLOYEE': '#98D8C8',
  'CREATE_WEBSITE': '#6C5CE7',
  'SOCIAL_POST': '#A29BFE',
  'MANAGE_INVOICES': '#74B9FF',
  'EXPAND_BUSINESS': '#81ECEC',
  'TODO_LIST': '#FDCB6E',
  'DOCUMENT_NODE': '#DFE6E9',
  'DELEGATION_NODE': '#B2BEB5',
  'COMMENT': '#E8DAEF',
  'NOVEL_NODE': '#F8B88B',
  'Research': '#4ECDC4',
  'Planning': '#45B7D1',
  'default': '#95A5A6'
};

// Phase colors for roadmap visualization
const phaseColorMap = {
  'PLANNING': '#6366F1',
  'FOUNDATION': '#8B5CF6',
  'DEVELOPMENT': '#EC4899',
  'LAUNCH': '#F59E0B',
  'GROWTH': '#10B981',
  'SCALING': '#06B6D4'
};

function getNodeColor(type) {
  return nodeColorMap[type] || nodeColorMap.default;
}

function getPhaseColor(phase) {
  return phaseColorMap[phase] || '#95A5A6';
}

// Stream workflow generation - calls backend which calls Gemini
export async function* streamWorkflowFromPrompt(prompt, onNodeReceived) {
  const fullPrompt = `Create a comprehensive business execution plan for: "${prompt}"

You are a business strategist, project manager, and process automation specialist. Create a DETAILED, ERROR-FREE business execution plan.

Requirements:
- Create 10-15 granular, actionable nodes organized into clear phases (PLANNING, FOUNDATION, DEVELOPMENT, LAUNCH, GROWTH, SCALING)
- Each node must be atomic - one clear action, not multiple combined tasks
- Assign each task to a specific role (e.g., 'Founder', 'Legal Counsel', 'Marketing Manager', 'Financial Advisor', 'HR Manager', 'Tech Lead', 'Sales Director')
- Include realistic timelines and resource estimates
- Establish clear dependencies showing which tasks must be completed before others
- Include success criteria for each task

Respond ONLY with a valid JSON object (no other text before or after) containing:
{
  "nodes": [array of workflow nodes],
  "projectMetadata": {
    "totalEstimatedTime": "string",
    "criticalPath": ["array of node labels"],
    "keyMilestones": [{"name": "string", "targetDate": "string", "deliverables": ["array"]}],
    "teamRoles": [{"role": "string", "responsibilities": ["array"], "requiredSkills": ["array"]}],
    "documentChecklist": [{"category": "string", "documents": ["array"]}]
  }
}

Each node should have: type, label, description, assignedTo, priority, estimatedDuration, phase, successCriteria, requiredDocuments, todoItems.`;

  try {
    console.log("Calling backend for workflow generation");
    
    const response = await fetch(`${BACKEND_URL}api/gemini/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        prompt: fullPrompt,
        temperature: 0.7,
        max_tokens: 2048,
      }),
    });

    if (!response.ok) {
      throw new Error(`Backend error: ${response.statusText}`);
    }

    const data = await response.json();
    let jsonString = data.response.trim();
    
    // Extract JSON from response if it contains extra text
    // Look for JSON object or array
    const jsonMatch = jsonString.match(/\{[\s\S]*\}|\[[\s\S]*\]/);
    if (jsonMatch) {
      jsonString = jsonMatch[0];
    }
    
    // Parse JSON - with fallback repair
    let parsed;
    try {
      parsed = JSON.parse(jsonString);
    } catch (e) {
      console.error('JSON parse error, attempting repair:', e.message);
      // Try to fix common issues
      jsonString = jsonString
        .replace(/'/g, '"') // Single quotes to double quotes
        .replace(/,\s*}/g, '}') // Remove trailing commas
        .replace(/,\s*]/g, ']')
        .replace(/:\s*undefined/g, ': null')
        .replace(/\n/g, ' '); // Remove newlines
      parsed = JSON.parse(jsonString);
    }
    
    let nodes = Array.isArray(parsed) ? parsed : parsed.nodes || [];

    // Yield nodes one by one
    for (const item of nodes) {
      if (item && typeof item === 'object' && item.type) {
        const baseNode = {
          type: item.type,
          label: item.label || item.name || 'Untitled',
          phase: item.phase || "PLANNING",
          assignedTo: item.assignedTo || '',
          priority: item.priority || 'MEDIUM',
          estimatedDuration: item.estimatedDuration || '',
          successCriteria: item.successCriteria || '',
          requiredDocuments: item.requiredDocuments || [],
          todoItems: item.todoItems || [],
          color: getNodeColor(item.type),
        };
        
        if (item.type === 'TODO_LIST' && item.items) {
          baseNode.items = item.items;
        }
        
        yield baseNode;
        if (onNodeReceived) onNodeReceived(baseNode);
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }

    return { 
      metadata: parsed.projectMetadata || {
        totalEstimatedTime: '',
        criticalPath: [],
        keyMilestones: [],
        teamRoles: [],
        documentChecklist: []
      }
    };
  } catch (error) {
    console.error("Error in streamWorkflowFromPrompt:", error);
    throw error;
  }
}

// Generate complete workflow - calls backend which calls Gemini
export async function generateWorkflowFromPrompt(prompt) {
  const fullPrompt = `Create a comprehensive business execution plan for: "${prompt}"

You are a business strategist, project manager, and process automation specialist. Create a DETAILED, ERROR-FREE business execution plan.

Requirements:
- Create 10-15 granular, actionable nodes organized into clear phases (PLANNING, FOUNDATION, DEVELOPMENT, LAUNCH, GROWTH, SCALING)
- Each node must be atomic - one clear action, not multiple combined tasks
- Assign each task to a specific role (e.g., 'Founder', 'Legal Counsel', 'Marketing Manager', 'Financial Advisor', 'HR Manager', 'Tech Lead', 'Sales Director')
- Include realistic timelines and resource estimates
- Establish clear dependencies showing which tasks must be completed before others
- Include success criteria for each task

Respond ONLY with a valid JSON object (no other text before or after) containing:
{
  "nodes": [array of workflow nodes],
  "projectMetadata": {
    "totalEstimatedTime": "string",
    "criticalPath": ["array of node labels"],
    "keyMilestones": [{"name": "string", "targetDate": "string", "deliverables": ["array"]}],
    "teamRoles": [{"role": "string", "responsibilities": ["array"], "requiredSkills": ["array"]}],
    "documentChecklist": [{"category": "string", "documents": ["array"]}]
  }
}

Each node should have: type, label, description, assignedTo, priority, estimatedDuration, phase, successCriteria, requiredDocuments, todoItems.`;

  try {
    console.log("Calling backend for workflow generation");
    
    const response = await fetch(`${BACKEND_URL}api/gemini/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        prompt: fullPrompt,
        temperature: 0.7,
        max_tokens: 2048,
      }),
    });

    if (!response.ok) {
      throw new Error(`Backend error: ${response.statusText}`);
    }

    const data = await response.json();
    let jsonString = data.response.trim();
    
    // Extract JSON from response if it contains extra text
    // Look for JSON object or array
    const jsonMatch = jsonString.match(/\{[\s\S]*\}|\[[\s\S]*\]/);
    if (jsonMatch) {
      jsonString = jsonMatch[0];
    }
    
    // Parse JSON - with fallback repair
    let parsed;
    try {
      parsed = JSON.parse(jsonString);
    } catch (e) {
      console.error('JSON parse error, attempting repair:', e.message);
      // Try to fix common issues
      jsonString = jsonString
        .replace(/'/g, '"') // Single quotes to double quotes
        .replace(/,\s*}/g, '}') // Remove trailing commas
        .replace(/,\s*]/g, ']')
        .replace(/:\s*undefined/g, ': null')
        .replace(/\n/g, ' '); // Remove newlines
      parsed = JSON.parse(jsonString);
    }
    
    let nodes = Array.isArray(parsed) ? parsed : parsed.nodes || [];

    // Add colors and metadata to nodes
    const nodesWithColors = nodes.map(item => ({
      ...item,
      color: getNodeColor(item.type),
      phaseColor: getPhaseColor(item.phase || 'PLANNING'),
      successCriteria: item.successCriteria || '',
      requiredDocuments: item.requiredDocuments || [],
      todoItems: item.todoItems || [],
    }));

    return {
      nodes: nodesWithColors,
      metadata: parsed.projectMetadata || {
        totalEstimatedTime: '',
        criticalPath: [],
        keyMilestones: [],
        teamRoles: [],
        documentChecklist: []
      },
    };
  } catch (error) {
    console.error("Error generating workflow from prompt:", error);
    throw new Error(`Failed to generate workflow: ${error.message}`);
  }
}

export { getNodeColor, getPhaseColor };

// Generate single node - calls backend which calls Gemini
export async function generateNodeFromPrompt(prompt) {
  const fullPrompt = `Create a single workflow node for: "${prompt}"

Respond ONLY with a valid JSON object (no other text before or after) representing a single workflow node with: type, label, description, assignedTo, priority, estimatedDuration.`;

  try {
    console.log("Calling backend for node generation");
    
    const response = await fetch(`${BACKEND_URL}api/gemini/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        prompt: fullPrompt,
        temperature: 0.7,
        max_tokens: 2048,
      }),
    });

    if (!response.ok) {
      throw new Error(`Backend error: ${response.statusText}`);
    }

    const data = await response.json();
    let jsonString = data.response.trim();
    
    // Extract JSON from response if it contains extra text
    // Look for JSON object or array
    const jsonMatch = jsonString.match(/\{[\s\S]*\}|\[[\s\S]*\]/);
    if (jsonMatch) {
      jsonString = jsonMatch[0];
    }
    
    // Parse JSON - with fallback repair
    let parsed;
    try {
      parsed = JSON.parse(jsonString);
    } catch (e) {
      console.error('JSON parse error, attempting repair:', e.message);
      // Try to fix common issues
      jsonString = jsonString
        .replace(/'/g, '"') // Single quotes to double quotes
        .replace(/,\s*}/g, '}') // Remove trailing commas
        .replace(/,\s*]/g, ']')
        .replace(/:\s*undefined/g, ': null')
        .replace(/\n/g, ' '); // Remove newlines
      parsed = JSON.parse(jsonString);
    }
    
    return parsed;
  } catch (error) {
    console.error("Error generating node from prompt:", error);
    throw new Error(`Failed to generate node: ${error.message}`);
  }
}
